import { ViewMode } from '../core/types';

export function haptic(pattern: number | number[] = 10): void {
  if ('vibrate' in navigator) {
    navigator.vibrate(pattern);
  }
}

export function hapticHeavy(): void {
  haptic([15, 30, 15]);
}

export function hapticSuccess(): void {
  haptic([10, 50, 20]);
}

interface PullToRefreshOptions {
  container: HTMLElement;
  onRefresh: () => Promise<void> | void;
  threshold?: number;
}

export function initPullToRefresh({ container, onRefresh, threshold = 80 }: PullToRefreshOptions): () => void {
  const indicator = document.createElement('div');
  indicator.className = 'pull-indicator';
  indicator.innerHTML = `
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
    </svg>
  `;
  document.body.appendChild(indicator);

  let startY = 0;
  let pulling = false;
  let refreshing = false;

  function onTouchStart(e: TouchEvent) {
    if (refreshing) return;
    if (window.scrollY > 5) return;
    startY = e.touches[0].clientY;
    pulling = true;
  }

  function onTouchMove(e: TouchEvent) {
    if (!pulling || refreshing) return;
    const dy = e.touches[0].clientY - startY;
    if (dy < 0) {
      pulling = false;
      indicator.classList.remove('visible');
      return;
    }
    const progress = Math.min(dy / threshold, 1);
    const translateY = Math.min(dy * 0.5, threshold * 0.8) - 60;
    indicator.style.transform = `translateX(-50%) translateY(${translateY}px)`;
    indicator.classList.toggle('visible', dy > 20);

    const svg = indicator.querySelector('svg') as SVGElement;
    if (svg) svg.style.transform = `rotate(${progress * 360}deg)`;

    if (dy > threshold) {
      e.preventDefault();
    }
  }

  async function onTouchEnd(e: TouchEvent) {
    if (!pulling || refreshing) return;
    const dy = e.changedTouches[0].clientY - startY;
    pulling = false;

    if (dy >= threshold) {
      refreshing = true;
      indicator.classList.add('refreshing');
      indicator.style.transform = `translateX(-50%) translateY(0px)`;
      haptic(15);
      await onRefresh();
      refreshing = false;
      indicator.classList.remove('refreshing');
    }

    indicator.classList.remove('visible');
    indicator.style.transform = `translateX(-50%) translateY(-60px)`;
  }

  container.addEventListener('touchstart', onTouchStart, { passive: true });
  container.addEventListener('touchmove', onTouchMove, { passive: false });
  container.addEventListener('touchend', onTouchEnd, { passive: true });

  return () => {
    container.removeEventListener('touchstart', onTouchStart);
    container.removeEventListener('touchmove', onTouchMove);
    container.removeEventListener('touchend', onTouchEnd);
    indicator.remove();
  };
}

interface SwipeNavOptions {
  container: HTMLElement;
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
  minDistance?: number;
  maxVertical?: number;
}

export function initSwipeNav({ container, onSwipeLeft, onSwipeRight, minDistance = 80, maxVertical = 60 }: SwipeNavOptions): () => void {
  let startX = 0;
  let startY = 0;
  let tracking = false;

  function onTouchStart(e: TouchEvent) {
    const touch = e.touches[0];
    startX = touch.clientX;
    startY = touch.clientY;
    tracking = true;
  }

  function onTouchEnd(e: TouchEvent) {
    if (!tracking) return;
    tracking = false;

    const touch = e.changedTouches[0];
    const dx = touch.clientX - startX;
    const dy = touch.clientY - startY;

    if (Math.abs(dy) > maxVertical) return;
    if (Math.abs(dx) < minDistance) return;

    if (dx > 0) {
      onSwipeRight();
    } else {
      onSwipeLeft();
    }
    haptic(8);
  }

  container.addEventListener('touchstart', onTouchStart, { passive: true });
  container.addEventListener('touchend', onTouchEnd, { passive: true });

  return () => {
    container.removeEventListener('touchstart', onTouchStart);
    container.removeEventListener('touchend', onTouchEnd);
  };
}

const VIEW_ORDER: ViewMode[] = ['dashboard', 'archive'];

export function getNextView(current: ViewMode, direction: 'left' | 'right'): ViewMode | null {
  const idx = VIEW_ORDER.indexOf(current);
  if (idx === -1) return null;
  if (direction === 'left' && idx < VIEW_ORDER.length - 1) return VIEW_ORDER[idx + 1];
  if (direction === 'right' && idx > 0) return VIEW_ORDER[idx - 1];
  return null;
}
